import request from '../utils/request';

/**
 * App 版本管理（管理端）
 *
 * ⚠️ 路由 / 菜单 index 是 appManage，但**接口前缀是 /sys/appVersion**，
 *    两者故意不同：index 决定侧边栏跳哪个路由，接口前缀决定打哪个后端方法。
 *
 * 业务定案（2026-10-05 用户拍板）：
 *   · 打包后在「小程序管理 → App版本管理」直接发布 1.0.1；
 *   · Android 弹窗 + 可跳过/强制 + 复制 apk 地址；
 *   · iOS **只弹提示**，后端对 iOS 返回 url=null，App 端也不给下载动作。
 */

/**
 * 版本列表（分页）
 * @param {Object} data { pageIndex, pageSize, platform, status, keyword }
 *        ⚠️ 本项目分页入参是 pageIndex/pageSize（从 1 开始），
 *           返回体总数字段是 dataCount（不是 total）—— 见 XcxQrcodeServiceImpl。
 */
export const page = (data) => {
    return request({
        url: '/sys/appVersion/page',
        method: 'post',
        data
    });
};

/**
 * 发布 / 编辑版本
 * @param {Object} data { id?, platform, version, forceVersion?, downloadUrl?, publishNote?, isMandatory?, status? }
 *        platform 只能 'iOS' | 'Android'；
 *        version 必须点分数字（后端有正则，填 "1.0-beta" 会被挡）；
 *        Android 的 downloadUrl 必填且须 http(s) 开头，iOS 可空。
 */
export const save = (data) => {
    return request({
        url: '/sys/appVersion/save',
        method: 'post',
        data
    });
};

/**
 * 启用 / 停用
 * ⚠️ 后端规则：同平台只允许一条 status=1，启用前若已有其它启用记录会直接拒绝。
 * @param {Number} id
 * @param {Number} status 1启用 0停用
 */
export const setStatus = (id, status) => {
    return request({
        url: '/sys/appVersion/setStatus',
        method: 'post',
        data: { id, status }
    });
};

/**
 * 逻辑删除
 * ⚠️ 后端规则：生效中（status=1）不允许删，必须先停用，否则返回失败。
 * @param {Number} id
 */
export const delAppVersion = (id) => {
    return request({
        url: '/sys/appVersion/delete',
        method: 'post',
        data: { id }
    });
};

/**
 * 上传 apk 安装包（发布弹框里顺手传，省掉「先传别处 → 回来粘地址」两步人工）
 *
 * 返回体 data = { url, fileName, size, sizeText }：
 *   · url      —— MinIO 上的 https 域名直链，回填到表单「下载地址」
 *   · sizeText —— 已换算好的「28.6 MB」，直接显示
 *
 * ⚠️ 三个必须注意的点：
 *   ① **不要手动设 Content-Type**。FormData 的 multipart 边界（boundary）必须由
 *      浏览器生成，写死 'multipart/form-data' 会丢掉 boundary → 后端解析不到文件、报 400。
 *      axios 0.x 对 FormData 会自动删除 Content-Type 交给浏览器，这里保持默认即可。
 *   ② 文件字段名固定 **file**（后端 @RequestParam("file")）。
 *   ③ platform / version 仅用于后端生成对象名（便于在 MinIO 里辨认版本），不影响存储位置，
 *      所以「先传包、后改版本号」也不会出错。
 *
 * @param {File}     file       选择的 .apk（≤100MB）
 * @param {Object}   extra      { platform, version } 仅用于对象名与日志
 * @param {Function} onProgress (percent: number 0-100) => void
 */
export const uploadApk = (file, extra, onProgress) => {
    const fd = new FormData();
    fd.append('file', file);
    if (extra && extra.platform) {
        fd.append('platform', extra.platform);
    }
    if (extra && extra.version) {
        fd.append('version', extra.version);
    }
    return request({
        url: '/sys/appVersion/uploadApk',
        method: 'post',
        data: fd,
        // 大包上传慢，别被默认超时打断（create 里已给 1000s，这里显式再声明一次意图）
        timeout: 1000000,
        onUploadProgress: (e) => {
            if (onProgress && e && e.total) {
                onProgress(Math.min(100, Math.round((e.loaded * 100) / e.total)));
            }
        }
    });
};
